/**
 * AuthService — unified authentication service for all platforms.
 *
 * Uses TokenProvider abstraction:
 *   Android → NativeBridgeTokenProvider (Kotlin SecureStorageModule)
 *   Web → WebTokenProvider (OAuthTokenStore with auto-refresh)
 *
 * All tools get tokens via getServiceClient() → AuthCoralHandler → authService.getAccessToken().
 */

import { isReactNativeApp } from '@amzn/jwo-store-management-platform-components'
import { LatencyMetric, CountMetric } from '@amzn/ihm-associate-tools-js-lib'
import { createTokenProvider, TokenProvider, TokenRefreshResult } from './TokenProvider'
import type { OAuthProviderConfig } from './web/types'
import { decodeJwtPayload } from '@utils/jwtUtils'
import { CrossOrgSignInError } from './CrossOrgSignInError'
import { SessionRefreshRejectedError } from './web/SessionRefreshRejectedError'

// Startup waits on the refresh of an expired restored session before showing anything, so it gets a shorter budget
// than the token store's default request timeout: past this, the request is aborted and the user is shown sign-in
// rather than a long blank wait.
export const STARTUP_REFRESH_TIMEOUT_MS = 5000

// Re-exported so existing importers (WebLoginPage, tests) keep importing from AuthService.
export { CrossOrgSignInError }

class AuthService {
    private static instance: AuthService
    private tokenProvider: TokenProvider
    private pendingTokenRequest: Promise<string> | null = null
    private cachedClientId: string | null = null
    private signingOutAfterFailedRefresh = false

    public static getInstance (): AuthService {
        if (!AuthService.instance) {
            AuthService.instance = new AuthService()
        }
        return AuthService.instance
    }

    /**
     * Initialize — restore session on web.
     */
    public async initialize (): Promise<void> {
        if (!isReactNativeApp()) {
            const { restoreSession, getStoredProviderConfig, isTokenExpired, clearSession } = await import('./web')
            const saved = restoreSession()
            if (saved) {
                this.tokenProvider.storeTokens(saved)
                const config = getStoredProviderConfig()
                if (config) this.tokenProvider.setProviderConfig(config)
                // The access token expired while the tab was idle, but the refresh token can still get a new one —
                // refresh now rather than sending the user back to sign in. If it does not succeed, the user sees
                // sign-in as before — except that a token with under a minute left (isTokenExpired allows a 60s margin)
                // is still used until it lapses, since isAuthenticated() has no margin. A rejected refresh only clears
                // the in-memory tokens, so also remove the saved session; otherwise a revoked refresh token would be
                // retried on every reload. A temporary failure (network, timeout, token endpoint 5xx) keeps it, so a
                // later reload can still restore the session.
                if (isTokenExpired(saved) && saved.refreshToken) {
                    const result = await this.refreshRestoredSession()
                    if (result.kind === 'failed') clearSession()
                }
            }
        } else {
            // Android: RN shell injects domainConfig as JSON into localStorage
            try {
                const raw = localStorage.getItem('domainConfig')
                if (raw) {
                    const dc = JSON.parse(raw)
                    if (typeof dc === 'object' && dc !== null && dc.poolId && dc.clientId) {
                        this.tokenProvider.setProviderConfig({
                            poolId: dc.poolId,
                            clientId: dc.clientId,
                            authorizationEndpoint: dc.authorizationEndpoint ?? '',
                            tokenEndpoint: dc.tokenEndpoint ?? '',
                            authType: dc.authType ?? 'COGNITO',
                        })
                    }
                }
            } catch (err) {
                console.warn('[AuthService] Failed to read domainConfig from localStorage:', err)
            }
            try {
                const token = await this.tokenProvider.getAccessToken()
                const payload = decodeJwtPayload(token)
                this.cachedClientId = payload?.client_id ?? null
            } catch (err) {
                console.warn('[AuthService] Failed to decode clientId from token:', err)
            }
        }
    }

    /**
     * Authenticate with a domain. On Android this is a no-op (RN already authenticated).
     * On web, triggers OAuth popup flow.
     */
    public async authenticate (domain: string): Promise<void> {
        if (isReactNativeApp()) return

        const loginLatency = new LatencyMetric(undefined, 'Time', 'JWOStoreManagementPlatform', 'Login.OAuth.Latency')
        loginLatency.start()
        try {
            const config = await this.fetchProviderConfig(domain)
            const { authenticate: popupAuthenticate, persistSession } = await import('./web')
            const tokens = await popupAuthenticate(config, domain)
            // Store tokens + config first so the cross-org guard's getCurrentUser call is authenticated.
            this.tokenProvider.storeTokens(tokens)
            this.tokenProvider.setProviderConfig(config)
            // Golden Gate cross-org sign-in guard: reject the sign-in if the authenticated user
            // does not belong to the org that owns the entered domain. Runs BEFORE persistSession
            // so a rejected attempt leaves no session behind.
            try {
                await this.assertOrgMembership(config.organizationId, domain)
            } catch (guardError) {
                // Cleanup only — don't redirect to /logout, so WebLoginPage can show the rejection.
                await this.logout({ redirect: false })
                throw guardError
            }
            persistSession(tokens, config, domain)
        } catch (error) {
            // A cross-org rejection is an expected user error (already tracked via
            // Login.CrossOrgRejected in assertOrgMembership), not an OAuth/login system fault, so
            // don't inflate the fault metrics that feed login-failure alarms (AutoSDE, CR-298484062).
            if (!(error instanceof CrossOrgSignInError)) {
                new CountMetric(undefined, 'Fault', 'JWOStoreManagementPlatform', 'Login.OAuth.Failure').count(1)
                new CountMetric(undefined, 'Fault', 'JWOStoreManagementPlatform', 'Login.Failure').count(1)
            }
            throw error
        } finally {
            loginLatency.stop()
        }
    }

    /**
     * Check if user is authenticated.
     */
    public async isAuthenticated (): Promise<boolean> {
        return this.tokenProvider.isAuthenticated()
    }

    /**
     * Get access token. Coalesces concurrent calls.
     */
    public async getAccessToken (): Promise<string> {
        if (this.pendingTokenRequest) return this.pendingTokenRequest
        this.pendingTokenRequest = this.tokenProvider.getAccessToken()
        try {
            return await this.pendingTokenRequest
        } catch (error) {
            // The proactive refresh was rejected, so the session has just ended. Sign out here, whichever caller
            // asked for the token: the refresh runs before any request is sent, so no 401 would reach the
            // sign-out in AuthCoralHandler, and every later call would fail with no token.
            if (error instanceof SessionRefreshRejectedError) this.signOutAfterFailedRefresh()
            throw error
        } finally {
            this.pendingTokenRequest = null
        }
    }

    /**
     * Ends the session once a token refresh has been rejected — the forced refresh after a gateway 401, or the
     * proactive refresh inside getAccessToken(). Many in-flight calls can fail together, so only the first one
     * signs out; logout() then navigates to the sign-in screen.
     */
    public signOutAfterFailedRefresh (): void {
        if (this.signingOutAfterFailedRefresh) return
        this.signingOutAfterFailedRefresh = true
        this.logout()
            .then((navigated) => {
                // With no provider config to build the Cognito /logout URL from, logout() only clears local state,
                // and nothing else re-renders the app — every later call would fail with no token. Reload from the
                // bare origin instead, which boots unauthenticated onto the sign-in screen. The guard stays set: the
                // page is unloading, and resetting it would let each 401 in that window sign out again.
                if (!navigated) window.location.assign(window.location.origin)
            })
            .catch((error) => {
                console.error('[SMP] Sign-out after a failed token refresh did not complete', error)
                // Nothing navigated, so let a later failed refresh try again.
                this.signingOutAfterFailedRefresh = false
            })
    }

    /**
     * Force a token refresh after the gateway rejected the current token as unauthorized.
     * 'unsupported' when the platform's provider does not refresh on demand (React Native).
     */
    public async forceRefreshAccessToken (): Promise<TokenRefreshResult> {
        if (!this.tokenProvider.forceRefresh) return { kind: 'unsupported' }
        return this.tokenProvider.forceRefresh()
    }

    /**
     * Get cached provider config (poolId, clientId, etc.).
     * Lazily fetches from gateway if not cached.
     */
    public async getProviderConfig (domain?: string): Promise<OAuthProviderConfig | null> {
        const cached = this.tokenProvider.getProviderConfig()
        if (cached) return cached

        try {
            const config = await this.fetchProviderConfig(domain || '')
            this.tokenProvider.setProviderConfig(config)
            return config
        } catch {
            return null
        }
    }

    /**
     * Get cached provider config synchronously (for headers).
     */
    public getProviderConfigSync (): OAuthProviderConfig | null {
        return this.tokenProvider.getProviderConfig()
    }

    /**
     * Get client_id synchronously. On Android, decoded from JWT during initialize().
     * On web, read from provider config.
     */
    public getClientId (): string | null {
        return this.cachedClientId ?? this.tokenProvider.getProviderConfig()?.clientId ?? null
    }

    /**
     * Logout — clear tokens and session.
     *
     * Web (Cognito/OAuth): clear local state, then redirect the top window to the
     * Cognito Hosted UI /logout endpoint. Clearing only the local token provider +
     * persisted OAuth session leaves the Cognito session cookie intact, and
     * `prompt=login` on the next /authorize (see CognitoProvider) is not always
     * honored by the Hosted UI — so the next sign-in could silently re-issue a
     * code and land the previous user on the home screen with no credential
     * prompt (P494835427, shared-desktop risk). A TOP-LEVEL navigation to /logout
     * clears the cookie as a first-party request, which works in every browser; a
     * hidden iframe cannot (its request is third-party and is blocked by Safari
     * ITP / Chrome's third-party-cookie phase-out). We clear local state FIRST so
     * the app boots unauthenticated on return from the bare origin and renders
     * WebLoginPage (no ?domain/region/storeId needed — the logged-out login
     * screen, not the provision "A domain is required" error).
     *
     * `redirect` (default true) is the user-initiated Sign-Out path. Internal
     * cleanup callers (e.g. the cross-org sign-in guard) pass `false` to clear
     * local state without navigating away, so their error UI can surface. When
     * no Cognito provider config is available the redirect is skipped entirely.
     * React Native is unchanged (native shell owns Cognito session teardown).
     * Resolves to true when it navigated to the Cognito /logout URL.
     */
    public async logout (options: { redirect?: boolean } = {}): Promise<boolean> {
        const { redirect = true } = options
        if (!isReactNativeApp()) {
            // Capture provider config + build the URL before clearing — clear() nulls the config.
            const config = this.tokenProvider.getProviderConfig()
            const { clearSession, buildCognitoLogoutUrl } = await import('./web')
            const logoutUrl = redirect && config ? buildCognitoLogoutUrl(config) : null
            // Cognito /logout only ends the Hosted UI session. Revoke the refresh token too, before clear() drops it,
            // or a copy taken before sign-out keeps working for its full lifetime. Best effort: local cleanup runs regardless.
            try {
                await this.tokenProvider.revoke?.()
            } catch (error) {
                console.warn('[AuthService] Refresh token revoke failed', error)
            }
            await this.tokenProvider.clear()
            clearSession()
            if (logoutUrl) window.location.assign(logoutUrl)
            return logoutUrl !== null
        }
        await this.tokenProvider.clear()
        return false
    }

    /**
     * Resolve the signed-in user's display name and email for UI (e.g. navbar profile).
     *
     * Web (Cognito/OAuth): decoded from the OIDC ID token claims. Falls back to
     * the email local-part for the name, then to empty strings if unavailable.
     * React Native: the native shell persists `userEmail` in localStorage.
     */
    public async getUserProfile (): Promise<{ name: string; email: string }> {
        if (isReactNativeApp()) {
            const email = (typeof localStorage !== 'undefined' && localStorage.getItem('userEmail')) || ''
            return { name: email, email }
        }

        try {
            const { oauthTokenStore } = await import('./web/OAuthTokenStore')
            const idToken = oauthTokenStore.getIdToken()
            if (!idToken) return { name: '', email: '' }

            const claims = decodeJwtPayload(idToken) || {}
            const email: string = claims.email ?? ''
            const name: string =
                claims.name ??
                claims['cognito:username'] ??
                (email ? email.split('@')[0] : '')
            return { name, email }
        } catch (err) {
            console.warn('[AuthService] Failed to resolve user profile:', err)
            return { name: '', email: '' }
        }
    }

    /**
     * Refreshes an expired session restored on reload, giving up after STARTUP_REFRESH_TIMEOUT_MS. The request is
     * aborted at that point rather than left running, so a late response cannot overwrite or clear a session the user
     * has signed in to since; giving up counts as a temporary failure. Emits SessionRestore.Refresh.* so it is visible
     * how often a reload recovers a session.
     */
    private async refreshRestoredSession (): Promise<TokenRefreshResult> {
        if (!this.tokenProvider.forceRefresh) return { kind: 'unsupported' }
        const latency = new LatencyMetric(undefined, 'Time', 'JWOStoreManagementPlatform', 'SessionRestore.Refresh.Latency')
        latency.start()
        const result = await this.tokenProvider.forceRefresh({ timeoutMs: STARTUP_REFRESH_TIMEOUT_MS })
        latency.stop()
        new CountMetric(undefined, 'Count', 'JWOStoreManagementPlatform', 'SessionRestore.Refresh.Success').count(result.kind === 'refreshed' ? 1 : 0)
        new CountMetric(undefined, 'Count', 'JWOStoreManagementPlatform', 'SessionRestore.Refresh.Failed').count(result.kind === 'failed' ? 1 : 0)
        new CountMetric(undefined, 'Count', 'JWOStoreManagementPlatform', 'SessionRestore.Refresh.Transient').count(result.kind === 'transient' ? 1 : 0)
        return result
    }

    /**
     * Fetch provider config from gateway. Provider-agnostic — the gateway
     * returns the config, and the IOAuthProvider knows how to use it.
     *
     * The domain registry is partitioned per cell and each gateway reads only its own partition, so
     * a tenant registered in the other US cell answers ResourceNotFoundException here — which is
     * indistinguishable from an unregistered domain unless we ask the peer too. The React Native
     * shell already does this fallback before it hands SMP a domainConfig; web has no such step, so
     * it does the fan-out itself. Non-US cells have no peer and make no extra call.
     */
    private async fetchProviderConfig (domain: string): Promise<OAuthProviderConfig> {
        const { createUnauthenticatedGatewayClient, createUnauthenticatedGatewayClientForRealm } =
            await import('@api/client-factory/UnauthenticatedClientFactory')
        const { GetDomainConfigurationInput } = await import('@amzn/coral_com-amazon-jwostoremanagementgateway')
        const { getPeerRealms } = await import('@utils/RegionRealms')
        const { isCoralException } = await import('@utils/CoralErrors')
        const input = GetDomainConfigurationInput.clone({ domain })

        let result
        try {
            result = await createUnauthenticatedGatewayClient().getDomainConfiguration(input).toPromise()
        } catch (err: any) {
            if (!isCoralException(err, 'ResourceNotFoundException')) throw err
            result = await this.fetchProviderConfigFromPeers(domain, getPeerRealms(), err, (realm) =>
                createUnauthenticatedGatewayClientForRealm(realm).getDomainConfiguration(input).toPromise(),
            )
        }

        const cognitoDomain = result.cognitoDomain
        const region = result.region
        const baseUrl = `https://${cognitoDomain}.auth.${region}.amazoncognito.com/oauth2`

        return {
            poolId: result.poolId,
            clientId: result.clientId,
            authorizationEndpoint: `${baseUrl}/authorize`,
            tokenEndpoint: `${baseUrl}/token`,
            authType: result.authType,
            // `organizationId` is a new GetDomainConfigurationOutput field; read it defensively so
            // this compiles against the currently-published coral client (the model change ships in
            // a separate CR). Populated at runtime once the gateway revision is deployed.
            organizationId: (result as { organizationId?: string }).organizationId,
            providerMetadata: { cognitoDomain, region },
        }
    }

    /**
     * Asks each peer cell for a domain the pinned cell does not know, returning the first answer.
     *
     * Rethrows the pinned cell's error when no peer has it either: to the user this is "unknown
     * domain", and that is the cell whose failure their next attempt will repeat.
     *
     * Only ResourceNotFoundException means "not registered in this cell", the same rule
     * getStoreInformation follows. A throttle or 5xx from the cell that does own the domain has to
     * surface as itself, or a transient fault reaches the user as an unknown domain.
     *
     * Deliberately does not re-pin the session to the answering cell. A tenant's domain row and its
     * stores can live in different cells, and getStoreInformation resolves and records the store's
     * cell on selection, which is what the rest of the session keys off.
     */
    private async fetchProviderConfigFromPeers<T> (
        domain: string,
        realms: string[],
        pinnedError: any,
        fetch: (realm: string) => Promise<T>,
    ): Promise<T> {
        const { isCoralException } = await import('@utils/CoralErrors')

        for (const realm of realms) {
            try {
                const result = await fetch(realm)
                console.log(`[AuthService] Domain ${domain} resolved in peer cell ${realm}`)
                return result
            } catch (err) {
                if (!isCoralException(err, 'ResourceNotFoundException')) throw err
                console.warn(`[AuthService] Domain ${domain} not in peer cell ${realm}`, err)
            }
        }
        throw pinnedError
    }

    /**
     * Cross-org sign-in guard (Golden Gate shared pool). Verifies the authenticated caller
     * belongs to the org that owns the entered domain, by comparing that org's id (resolved
     * server-side from the entered domain via GetDomainConfiguration) against the caller's
     * org set from GetCurrentUser. Comparing by org id — not domain string — is robust for
     * multi-domain orgs (a valid secondary domain still resolves to the same org id).
     *
     * Fail-open (do NOT block) when membership can't be positively disproven: the gateway
     * didn't return an owning org id (older revision pre-rollout), getCurrentUser errors, or
     * the caller's org set is empty/unresolved. Backend AVP still scopes all data access per
     * org in those cases. Fail-closed only on a positive mismatch — the caller has org(s) and
     * none is the entered domain's org.
     *
     * @param expectedOrgId owning org id of the entered domain (from GetDomainConfiguration)
     * @param enteredDomain the domain the user signed in with (for the error message)
     */
    private async assertOrgMembership (expectedOrgId: string | undefined, enteredDomain: string): Promise<void> {
        // Domain's owning org unknown (older gateway not returning organizationId) → fail open.
        if (!expectedOrgId) return
        let organizations: Array<{ id?: string }> = []
        try {
            const { getCurrentUser } = await import('@api/userManagement')
            const result = await getCurrentUser()
            organizations = result.organizations ?? []
        } catch (err) {
            console.warn('[AuthService] cross-org guard: getCurrentUser failed, skipping check', err)
            return
        }
        const orgIds = organizations
            .map((o) => o.id)
            .filter((id): id is string => typeof id === 'string' && id.length > 0)
        // Caller's org set unknown/empty (e.g. org routing off) → cannot disprove → fail open.
        if (orgIds.length === 0) return
        if (!orgIds.includes(expectedOrgId)) {
            new CountMetric(undefined, 'Fault', 'JWOStoreManagementPlatform', 'Login.CrossOrgRejected').count(1)
            throw new CrossOrgSignInError(enteredDomain)
        }
    }

    private constructor () {
        this.tokenProvider = createTokenProvider()
    }
}

export const authService = AuthService.getInstance()
